import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '../context/SessionContext';

/**
 * Custom hook for handling abandon confirmation functionality
 * @param {string} instanceId - The instance ID (questionnaire or challenge instance) for the API call
 * @param {string} eid - The eval ID for redirection
 * @param {string} abandonEndpoint - The API endpoint for abandoning
 * @param {string} statusKey - The key to update in session data (e.g., 'questionnaire_status')
 * @param {string} statusValue - The value to set for the status (e.g., 'qs_abandoned')
 * @returns {Object} Object containing abandon confirmation state and handler functions
 */
export const useAbandonConfirmation = (instanceId, eid, abandonEndpoint, statusKey, statusValue) => {
  const navigate = useNavigate();
  const { updateSessionData } = useSession();
  const [showAbandonConfirm, setShowAbandonConfirm] = useState(false);
  const [error, setError] = useState(null);
  
  const handleAbandonClick = () => {
    setShowAbandonConfirm(true);
  };
  
  const handleCancelAbandon = () => {
    setShowAbandonConfirm(false);
  };
  
  const handleConfirmAbandon = () => {
    // Send request to abandon using the instance ID
    fetch(`${abandonEndpoint}?id=${instanceId}`, {
      method: 'GET'
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`Failed to abandon: ${response.status} ${response.statusText}`);
      }
      return response.json();
    })
    .then(data => {
      // Update status
      const statusUpdate = { [statusKey]: statusValue };
      updateSessionData(statusUpdate);
      
      // Redirect to record page
      setTimeout(() => {
        navigate(`/${eid}`);  
      }, 1000);      
    })
    .catch(err => {
      console.error('Error abandoning:', err);
      setError(err.message);
    })
    .finally(() => {
      setShowAbandonConfirm(false);
    });
  };
  
  return { 
    showAbandonConfirm, 
    error,
    handleAbandonClick, 
    handleCancelAbandon, 
    handleConfirmAbandon 
  };
};
